import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '@heroui/react';
import {
  RiArrowLeftLine,
  RiArrowRightLine,
  RiExternalLinkLine,
  RiAddLine,
  RiStickyNoteLine,
  RiChat3Line,
  RiContactsLine,
  RiErrorWarningLine
} from '@remixicon/react';
import { useAtom, useAtomValue } from 'jotai';
import { leadsViewerIdsAtom, leadsViewerQueryAtom } from '@/store';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { LeadScore } from '@/components/leads/LeadScore';
import { AddNoteModal } from '@/components/leads/AddNoteModal';
import { AddInteractionModal } from '@/components/leads/AddInteractionModal';
import { AddContactModal } from '@/components/leads/AddContactModal';
import { useLeadNotes, useLeadInteractions, useLeadContacts } from '@/hooks/leads';
import type { Lead } from '@/types';

export default function LeadsViewerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leadIds, setLeadIds] = useAtom(leadsViewerIdsAtom);
  const queryResult = useAtomValue(leadsViewerQueryAtom);
  const { data, isLoading } = queryResult;
  const leads = React.useMemo<Lead[]>(() => data?.data ?? [], [data]);

  const [currentIndex, setCurrentIndex] = React.useState<number>(0);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState<boolean>(false);
  const [isIframeLoading, setIsIframeLoading] = React.useState<boolean>(true);
  const [frameError, setFrameError] = React.useState<{ domain: string; blocked: boolean } | null>(null);

  // Hooks for notes and interactions
  const {
    isAddModalOpen: isAddNoteModalOpen,
    selectedLead: selectedLeadForNote,
    isSubmitting: isSubmittingNote,
    openAddModal: openAddNoteModal,
    handleCreateNote,
    onAddModalOpenChange: onAddNoteModalOpenChange
  } = useLeadNotes();

  const {
    isAddModalOpen: isAddInteractionModalOpen,
    selectedLead: selectedLeadForInteraction,
    isSubmitting: isSubmittingInteraction,
    openAddModal: openAddInteractionModal,
    handleCreateInteraction,
    onAddModalOpenChange: onAddInteractionModalOpenChange
  } = useLeadInteractions();
  const {
    isAddModalOpen: isAddContactModalOpen,
    selectedLead: selectedLeadForContact,
    isSubmitting: isSubmittingContact,
    openAddModal: openAddContactModal,
    handleCreateContact,
    onAddModalOpenChange: onAddContactModalOpenChange
  } = useLeadContacts();

  // Get lead IDs from URL params and update atom
  const leadIdsParam = searchParams.get('ids');
  React.useEffect(() => {
    if (leadIdsParam) {
      const parsedIds = leadIdsParam
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));
      setLeadIds(parsedIds);
    } else {
      setLeadIds([]);
    }
  }, [leadIdsParam, setLeadIds]);

  // Reset to first lead when leads change
  React.useEffect(() => {
    if (leads.length > 0) {
      setCurrentIndex(0);
      setIsIframeLoading(true);
      setFrameError(null);
    }
  }, [leads.length]);

  // Calculate current lead before using it in effects
  const currentLead = leads[currentIndex];
  const hasNext = currentIndex < leads.length - 1;
  const hasPrev = currentIndex > 0;

  // Reset loading state when current lead changes
  React.useEffect(() => {
    if (currentLead) {
      setIsIframeLoading(true);
      setFrameError(null);
    }
  }, [currentLead?.id]);

  // Detect X-Frame-Options blocking
  React.useEffect(() => {
    if (!currentLead) return;

    // Listen for console errors related to X-Frame-Options
    const originalError = console.error;
    let xFrameErrorDetected = false;

    const errorHandler = (...args: any[]) => {
      const errorMessage = args.join(' ');
      if (
        errorMessage.includes('X-Frame-Options') ||
        errorMessage.includes('Refused to display') ||
        errorMessage.includes('in a frame')
      ) {
        xFrameErrorDetected = true;
        setIsIframeLoading(false);
        setFrameError({ domain: currentLead.domain, blocked: true });
      }
      originalError.apply(console, args);
    };

    console.error = errorHandler;

    // Also use timeout as fallback (10 seconds)
    const timeoutId = setTimeout(() => {
      if (isIframeLoading && !xFrameErrorDetected) {
        // If still loading after 10 seconds, might be blocked or very slow
        // Try to detect by checking if we can access iframe
        const iframe = document.querySelector(`iframe[title="${currentLead.domain}"]`) as HTMLIFrameElement;
        if (iframe) {
          try {
            // This will throw for cross-origin, but that's normal
            // We only care if it's completely blocked
            const hasContent = iframe.contentWindow !== null;
            if (!hasContent && isIframeLoading) {
              setIsIframeLoading(false);
              setFrameError({ domain: currentLead.domain, blocked: true });
            }
          } catch (e) {
            // Cross-origin is normal, don't treat as error
          }
        }
      }
    }, 10000); // 10 second timeout

    return () => {
      console.error = originalError;
      clearTimeout(timeoutId);
    };
  }, [currentLead, isIframeLoading]);

  const handlePrev = React.useCallback(() => {
    if (hasPrev) {
      setCurrentIndex((prev) => prev - 1);
      setIsIframeLoading(true);
      setFrameError(null);
    }
  }, [hasPrev]);

  const handleNext = React.useCallback(() => {
    if (hasNext) {
      setCurrentIndex((prev) => prev + 1);
      setIsIframeLoading(true);
      setFrameError(null);
    }
  }, [hasNext]);

  // Handle iframe load
  const handleIframeLoad = React.useCallback(() => {
    setIsIframeLoading(false);
    setFrameError(null);
  }, []);

  // Handle iframe error (X-Frame-Options or other blocking)
  const handleIframeError = React.useCallback(() => {
    setIsIframeLoading(false);
    if (currentLead) {
      setFrameError({ domain: currentLead.domain, blocked: true });
    }
  }, [currentLead]);

  // Handle close request - show confirm modal
  const handleCloseRequest = React.useCallback(() => {
    setIsConfirmOpen(true);
  }, []);

  // Handle confirmed close
  const handleConfirmClose = React.useCallback(() => {
    setIsConfirmOpen(false);
    window.close();
    // If window.close doesn't work, navigate back
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/relationships/leads');
    }
  }, [navigate]);

  // Handle ESC key
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isConfirmOpen) {
        handleCloseRequest();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isConfirmOpen, handleCloseRequest]);

  if (isLoading) {
    return (
      <div className="bg-background fixed inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-default-500 mb-4">Loading leads...</p>
        </div>
      </div>
    );
  }

  if (leadIds.length === 0) {
    return (
      <div className="bg-background fixed inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-default-500 mb-4">No lead IDs provided</p>
          <Button onPress={() => navigate('/relationships/leads')}>Go back to Leads</Button>
        </div>
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="bg-background fixed inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-default-500 mb-4">No leads found for the provided IDs</p>
          <Button onPress={() => navigate('/relationships/leads')}>Go back to Leads</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background fixed inset-0 flex flex-col">
      {/* Header */}
      <div className="border-default-200 bg-content1 flex items-center justify-between border-b p-4">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-semibold">
            {currentIndex + 1} / {leads.length}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-default-900 text-base font-medium">{currentLead?.domain}</span>
            {currentLead?.title && <span className="text-default-500 text-sm">- {currentLead.title}</span>}
            {currentLead && <LeadScore lead={currentLead} showLabel size="sm" />}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLead && (
            <>
              <PermissionGuard permission="LeadNote.store">
                <Button
                  size="sm"
                  variant="light"
                  color="warning"
                  startContent={<RiStickyNoteLine className="h-4 w-4" />}
                  onPress={() => openAddNoteModal(currentLead)}
                >
                  Add Note
                </Button>
              </PermissionGuard>
              <PermissionGuard permission="LeadContact.store">
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  startContent={<RiContactsLine className="h-4 w-4" />}
                  onPress={() => currentLead && openAddContactModal(currentLead)}
                >
                  Add Contact
                </Button>
              </PermissionGuard>
              <PermissionGuard permission="LeadInteraction.store">
                <Button
                  size="sm"
                  variant="light"
                  color="primary"
                  startContent={<RiChat3Line className="h-4 w-4" />}
                  onPress={() => openAddInteractionModal(currentLead)}
                >
                  Add Interaction
                </Button>
              </PermissionGuard>
              <Button
                size="sm"
                variant="light"
                startContent={<RiExternalLinkLine className="h-4 w-4" />}
                onPress={() => window.open(`https://${currentLead.domain}`, '_blank', 'noopener,noreferrer')}
              >
                Open in new tab
              </Button>
            </>
          )}
          <Button isIconOnly size="sm" variant="light" isDisabled={!hasPrev} onPress={handlePrev} aria-label="Previous">
            <RiArrowLeftLine className="h-4 w-4" />
          </Button>
          <Button isIconOnly size="sm" variant="light" isDisabled={!hasNext} onPress={handleNext} aria-label="Next">
            <RiArrowRightLine className="h-4 w-4" />
          </Button>
          <Button isIconOnly size="sm" variant="light" onPress={handleCloseRequest} aria-label="Close">
            ✕
          </Button>
        </div>
      </div>

      {/* Tab bar */}
      <div
        className="border-default-200 bg-content2 flex items-center gap-1 overflow-x-auto border-b p-2 [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none' // IE and Edge
        }}
      >
        {leads.map((lead, index) => (
          <button
            key={lead.id}
            onClick={() => setCurrentIndex(index)}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-all ${
              index === currentIndex
                ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                : 'bg-default-100 text-default-600 hover:bg-default-200'
            }`}
            title={lead.domain}
          >
            <span className="max-w-[200px] truncate">{lead.domain}</span>
          </button>
        ))}
      </div>

      {/* Iframe container */}
      <div className="relative min-h-0 flex-1">
        {currentLead && (
          <>
            {/* Loading overlay */}
            {isIframeLoading && !frameError && (
              <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm">
                <div className="flex flex-col items-center gap-4">
                  <Spinner size="lg" label="Loading website..." />
                  <p className="text-default-500 text-sm">{currentLead.domain}</p>
                </div>
              </div>
            )}

            {/* Frame blocked error */}
            {frameError && frameError.blocked && (
              <div className="bg-background absolute inset-0 z-10 flex items-center justify-center">
                <div className="mx-4 flex max-w-md flex-col items-center gap-4 text-center">
                  <RiErrorWarningLine className="text-warning h-16 w-16" />
                  <div>
                    <h3 className="mb-2 text-lg font-semibold">Cannot Display Website</h3>
                    <p className="text-default-600 text-sm">
                      This website ({frameError.domain}) cannot be displayed in a frame because it has set{' '}
                      <code className="bg-default-100 rounded px-1 text-xs">X-Frame-Options: deny</code>.
                    </p>
                    <p className="text-default-500 mt-2 text-xs">
                      This is a security measure set by the website owner.
                    </p>
                  </div>
                  <Button
                    color="primary"
                    startContent={<RiExternalLinkLine className="h-4 w-4" />}
                    onPress={() => window.open(`https://${frameError.domain}`, '_blank', 'noopener,noreferrer')}
                  >
                    Open in New Tab
                  </Button>
                </div>
              </div>
            )}

            {/* Iframe */}
            <iframe
              key={currentLead.id}
              src={`https://${currentLead.domain}`}
              className="h-full w-full border-0"
              title={currentLead.domain}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          </>
        )}
      </div>

      {/* Confirm Close Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-content1 mx-4 w-full max-w-md rounded-lg p-6">
            <h3 className="mb-4 text-lg font-semibold">Close Viewer</h3>
            <p className="text-default-600 mb-6">
              Are you sure you want to close the domain viewer? You will lose your current viewing position.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="light" onPress={() => setIsConfirmOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" onPress={handleConfirmClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onOpenChange={onAddNoteModalOpenChange}
        lead={selectedLeadForNote}
        isSubmitting={isSubmittingNote}
        onSubmit={(note) => handleCreateNote(note)}
      />
      <AddInteractionModal
        isOpen={isAddInteractionModalOpen}
        onOpenChange={onAddInteractionModalOpenChange}
        lead={selectedLeadForInteraction}
        isSubmitting={isSubmittingInteraction}
        onSubmit={(type, note, outcome) => handleCreateInteraction(type, note, outcome)}
      />
      <AddContactModal
        isOpen={isAddContactModalOpen}
        onOpenChange={onAddContactModalOpenChange}
        lead={selectedLeadForContact}
        isSubmitting={isSubmittingContact}
        onSubmit={(type, value, source) => handleCreateContact(type, value, source)}
      />
    </div>
  );
}
